<script>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';

import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  props: {
    template: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['sendMessage', 'resetTemplate'],
  setup(props, { emit }) {
    const processVariable = str => {
      return str.replace(/{{|}}/g, '');
    };

    const allKeysRequired = value => {
      const keys = Object.keys(value);
      return keys.every(key => value[key]);
    };

    const processedParams = ref({});
    const headerMediaUrl = ref('');

    const hasHeaderMedia = computed(() => {
      const headerComponent = props.template.components?.find(
        comp => comp.type === 'HEADER'
      );
      return (
        headerComponent &&
        ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerComponent.format)
      );
    });

    const headerMediaType = computed(() => {
      if (!hasHeaderMedia.value) return null;
      const headerComponent = props.template.components.find(
        comp => comp.type === 'HEADER'
      );
      return headerComponent?.format;
    });

    const templateString = computed(() => {
      return props.template.components.find(
        component => component.type === 'BODY'
      ).text;
    });

    const variables = computed(() => {
      return templateString.value.match(/{{([^}]+)}}/g);
    });

    const processedString = computed(() => {
      return templateString.value.replace(/{{([^}]+)}}/g, (match, variable) => {
        const variableKey = processVariable(variable);
        return processedParams.value[variableKey] || `{{${variable}}}`;
      });
    });

    const v$ = useVuelidate(
      {
        processedParams: {
          requiredIfKeysPresent: requiredIf(variables),
          allKeysRequired,
        },
        headerMediaUrl: {
          requiredIfMediaPresent: requiredIf(hasHeaderMedia),
        },
      },
      { processedParams, headerMediaUrl }
    );

    const generateVariables = () => {
      const matchedVariables = templateString.value.match(/{{([^}]+)}}/g);
      if (!matchedVariables) return;

      const finalVars = matchedVariables.map(i => processVariable(i));
      processedParams.value = finalVars.reduce((acc, variable) => {
        acc[variable] = '';
        return acc;
      }, {});
    };

    const resetTemplate = () => {
      emit('resetTemplate');
    };

    const sendMessage = () => {
      v$.value.$touch();
      if (v$.value.$invalid) return;

      let messageContent = processedString.value;

      // Add multimedia indicator if template has multimedia
      if (hasHeaderMedia.value) {
        const indicator = getMultimediaIndicator(headerMediaType.value);
        messageContent = `${indicator}\n\n${messageContent}`;
      }

      const payload = {
        message: messageContent,
        templateParams: {
          name: props.template.name,
          category: props.template.category,
          language: props.template.language,
          namespace: props.template.namespace,
          processed_params: processedParams.value,
        },
      };

      // Add header_handle if template has multimedia and URL is provided
      if (hasHeaderMedia.value && headerMediaUrl.value) {
        payload.templateParams.header_handle = headerMediaUrl.value;
      }

      emit('sendMessage', payload);
    };

    const getMultimediaIndicator = (type) => {
      const indicators = {
        IMAGE: '[📷 Imagen]',
        VIDEO: '[🎥 Video]',
        DOCUMENT: '[📄 Documento]',
      };
      return indicators[type] || '[📎 Archivo multimedia]';
    };

    onMounted(generateVariables);

    return {
      processedParams,
      variables,
      templateString,
      processedString,
      v$,
      resetTemplate,
      sendMessage,
      hasHeaderMedia,
      headerMediaType,
      headerMediaUrl,
    };
  },
};
</script>

<template>
  <div class="w-full">
    <textarea
      v-model="processedString"
      rows="4"
      readonly
      class="template-input"
    />
    <div v-if="variables || hasHeaderMedia" class="p-2.5">
      <!-- Header media URL input if template has multimedia -->
      <div
        v-if="hasHeaderMedia"
        class="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800"
      >
        <p class="text-xs font-medium text-blue-900 dark:text-blue-100 mb-2">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_REQUIRED', {
              type: headerMediaType,
            })
          }}
        </p>
        <woot-input
          v-model="headerMediaUrl"
          type="url"
          :placeholder="
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_PLACEHOLDER', {
              type: headerMediaType?.toLowerCase(),
            })
          "
          class="text-sm"
          :styles="{ marginBottom: 0 }"
        />
        <p class="text-xs text-blue-600 dark:text-blue-400 mt-1">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_HELP', {
              type: headerMediaType?.toLowerCase(),
            })
          }}
        </p>
      </div>

      <div v-if="variables">
        <p class="text-sm font-semibold mb-2.5">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        
        <div
          v-for="(variable, key) in processedParams"
          :key="key"
          class="items-center flex mb-2.5"
        >
          <span
            class="bg-n-alpha-black2 text-n-slate-12 inline-block rounded-md text-xs py-2.5 px-6"
          >
            {{ key }}
          </span>
          <woot-input
            v-model="processedParams[key]"
            type="text"
            class="flex-1 text-sm ml-2.5"
            :styles="{ marginBottom: 0 }"
          />
        </div>
      </div>

      <p
        v-if="v$.$dirty && v$.$invalid"
        class="bg-n-ruby-9/20 rounded-md text-n-ruby-9 p-2.5 text-center"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>
    <footer class="flex justify-end gap-2">
      <NextButton
        faded
        slate
        type="reset"
        :label="$t('WHATSAPP_TEMPLATES.PARSER.GO_BACK_LABEL')"
        @click="resetTemplate"
      />
      <NextButton
        type="button"
        :label="$t('WHATSAPP_TEMPLATES.PARSER.SEND_MESSAGE_LABEL')"
        @click="sendMessage"
      />
    </footer>
  </div>
</template>
